import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useAuth } from "@/features/auth";
import { LOGIN_PATH, LOGIN_ALIAS_PATH, LOGOUT_PATH } from "@/features/auth/routes";
import MandatoryFieldsGuard from "@/router/MandatoryFieldsGuard";
import { CenteredSpinner } from "@/shared/components/centeredSpinner";

export type ProtectedRouteProps = React.PropsWithChildren;

const RouteGuards = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated, refreshSession, isAuthFailed, isAuthenticating, isLegalComplianceRequired,
  } = useAuth();

  const isOnLoginPage = location.pathname === LOGIN_PATH || location.pathname === LOGIN_ALIAS_PATH;

  const getReturnUrl = () => {
    const previousPath = location.pathname;
    const returnPath = previousPath === LOGOUT_PATH ? "/" : previousPath;
    return `${returnPath}${location.search}${location.hash}`;
  };

  const navigateToLogin = () => navigate(LOGIN_PATH, { replace: true, state: { returnUrl: getReturnUrl() } });

  useEffect(() => {
    if (!isAuthenticated && !isAuthenticating && !isAuthFailed && !isLegalComplianceRequired) {
      refreshSession();
    }
  }, [isAuthenticated, isAuthenticating, isAuthFailed, isLegalComplianceRequired]);

  useEffect(() => {
    if (!isAuthFailed && !isLegalComplianceRequired) return;
    if (isOnLoginPage) return;
    navigateToLogin();
  }, [isAuthFailed, isLegalComplianceRequired, isOnLoginPage]);

  // Prevent rendering protected content until authentication fully resolves
  if (isAuthenticating || !isAuthenticated) {
    return (
      <div className="absolute top-0 left-0 right-0 bottom-0 pl-[270px] pt-[18px]">
        <CenteredSpinner />
      </div>
    );
  }

  return (
    <MandatoryFieldsGuard>
      {children}
    </MandatoryFieldsGuard>
  );
};

export default RouteGuards;
